// webService.js
import axios from 'axios';
import Cookies from 'js-cookie';
import { environment } from '@/env/env.prod';
import { getHeaders } from '@/api/requestHeaders';
import { clearClientSession } from '@/Auth/clearSiteSession';
const API_BASE = environment.BASE_URL;

export class WebService {
    constructor() {
        // Nothing about the session is captured here on purpose — see below.
        // Axios instance for consistent configuration
        this.client = axios.create({
            baseURL: API_BASE,
            timeout: 10000,
        });
    }

    // ── SESSION — read live, never stored ─────────────────────────────────
    // These used to be plain fields assigned in the constructor. Two of the
    // three places that build a WebService do it at module scope
    // (ClassRooms.tsx, TeacherSelectionDioalogBox.tsx), and that module is
    // reached by a fully static import chain from App.tsx — so the
    // constructor ran during initial bundle evaluation, before any login
    // could have happened in the tab. The instance then held nulls (or, after
    // an in-tab account switch, the previous account's values) for the rest
    // of the session: `fetchEntities` threw "Session missing! Please sign in
    // again." at a signed-in user, without ever issuing a request.
    //
    // As accessors, every read hits the cookie at that moment, so an instance
    // built before login is still correct after it. They are deliberately
    // getter-only: assigning to them is what caused the staleness, and a
    // TypeError is a better outcome than silently reintroducing it.
    get authToken() { return Cookies.get('authToken') || null; }
    get ins_code() { return Cookies.get('ins_code') || null; }
    get E_id() { return Cookies.get('E_id') || null; }
    get user_uid() { return Cookies.get('user_uid') || null; }
    get name() { return Cookies.get('name') || null; }
    get img() { return Cookies.get('img') || null; }
    get headers() { return getHeaders(); }

    // 🔹 Store session details in cookies
    #storeSession(data, authToken, ins_code, user_uid) {
        const fs_data = data?.firestoreUserData || {};
        Cookies.set('E_id', fs_data.E_id || '');
        Cookies.set('img', fs_data.img || '');
        Cookies.set('name', fs_data.name || '');
        Cookies.set('authToken', authToken);
        Cookies.set('ins_code', ins_code);
        Cookies.set('user_uid', user_uid);
        Cookies.set('login_time', new Date().toISOString());
    }

    // 🔹 Clear cookies (logout)
    clearSession() {
        clearClientSession();
    }

    // 🔹 Signin
    async signin({ authToken, id, ins_code, user_id }) {
        try {
            const params = {
                authToken,
                id,
                ins_code,
                designation: 1,
                dashboard_login: 1,
                device_token: 'null device token !',
            };

            //console.log(`[WebService] → Signin params:`, params);

            const response = await this.client.get('/signin', { params });
            const data = response.data;
            //console.log(`[WebService] ✅ Signin response:`, data);

            if (response.status === 200) {
                this.#storeSession(data, authToken, ins_code, user_id);
                return { success: true, data };
            }

            return { success: false, message: 'Signin failed' };
        } catch (error) {
            console.error('[WebService] ❌ Error in signin:', error.message);
            return { success: false, error: error.message };
        }
    }

    // 🔹 Fetch entities with pagination + search
    /**
      * @param {number} page_size - Items per page
      * @param {string|number} last_pagination_value - The cursor (name, timestamp, or ID)
      * @param {string} entity_search - Name search string
      * @param {string} query_entity_type - '1' (Faculty) or '0' (Student)
      * @param {boolean} only_unverified - Trigger is_verified index
      * @param {boolean} sort_by_date - Trigger created_at index
      */
    async fetchEntities({
        page_size = 10,
        last_pagination_value = null,
        entity_search = '',
        query_entity_type = '1', // Defaulting to Faculty
        only_unverified = false,
        sort_by_date = false,
        role = null
    } = {}) {
        try {
            if (!this.authToken || !this.ins_code || !this.E_id) {
                throw new Error('Session missing! Please sign in again.');
            }
            // Map frontend naming to the backend fetchUsers expected keys
            const params = {
                insCode: this.ins_code,
                designation: query_entity_type,
                pageSize: page_size,
                lastPageSnap: last_pagination_value,
                searchedName: entity_search,
                onlyUnverified: only_unverified,
                sortByDate: sort_by_date,
                role: role
            };
            // Using GET for retrieval
            const response = await this.client.get('/user/fetchUsers', {
                params,
                headers: this.headers
            });
            if (response.status === 200 && response.data.success) {
                return {
                    success: true,
                    data: {
                        results: response.data.users,
                        nextPageToken: response.data.lastPageSnap,
                        totalUsers: response.data.totalCount
                    }
                };
            }
            return { success: false, message: response.data.message || 'Failed to fetch entities' };
        } catch (error) {
            console.error('[WebService] ❌ Error in fetchEntities:', error.message);
            return { success: false, error: error.message };
        }
    }

    // 🔹 Basic entity count (matches backend)
    async getEntityCount(entityType) {
        try {
            if (!this.authToken || !this.ins_code) {
                throw new Error('Session missing! Please sign in again.');
            }

            const body = {
                parent: this.ins_code,
                entityType, // "faculty", "student", "admin", etc.
            };

            //console.log('[WebService] → Fetching entity count:', body);

            const response = await this.client.post('/web/getEntityCount', body, {
                headers: this.headers
            });

            if (response.status === 200) {
                return { success: true, data: response.data };
            }

            return { success: false, message: 'Failed to fetch entity count' };
        } catch (error) {
            console.error('[WebService] ❌ Error in getEntityCount:', error.message);
            return { success: false, error: error.message };
        }
    }


    // 🔹 Get filtered entity count (by role, name_lower, or fac)
    async getFilteredEntityCount(filters = {}) {
        try {
            if (!this.authToken || !this.ins_code) {
                throw new Error('Session missing! Please sign in again.');
            }

            const body = {
                parent: this.ins_code,
                filters,
            };

            //console.log('[WebService] → Fetching filtered entity count:', body);

            const response = await this.client.post('/web/getFilteredEntityCount', body, {
                headers: this.headers
            });

            if (response.status === 200) {
                return { success: true, data: response.data };
            }

            return { success: false, message: 'Failed to fetch filtered entity count' };
        } catch (error) {
            console.error('[WebService] ❌ Error in getFilteredEntityCount:', error.message);
            return { success: false, error: error.message };
        }
    }

    // 🔹 Utility — Get all session data
    getSession() {
        return {
            authToken: this.authToken,
            ins_code: this.ins_code,
            E_id: this.E_id,
            user_uid: this.user_uid,
            name: this.name,
            img: this.img,
            login_time: Cookies.get('login_time'),
        };
    }

    // 🔹 Promote user to Admin
    async createAdmin(f_id, e_id) {
        try {
            if (!this.authToken || !this.ins_code) {
                throw new Error('Session missing! Please sign in again.');
            }

            const body = {
                parent: this.ins_code,
                uidToOperate: f_id,
                e_id: e_id,
            };

            //console.log('[WebService] → Creating Admin:', body);

            const response = await this.client.post('/web/createAdmin', body, {
                headers: this.headers,
            });

            if (response.status === 200) {
                //console.log('[WebService] ✅ Admin created:', response.data);
                return { success: true, data: response.data };
            }

            return { success: false, message: 'Failed to create admin' };
        } catch (error) {
            console.error('[WebService] ❌ Error in createAdmin:', error.message);
            return { success: false, error: error.message };
        }
    }

    // 🔹 Promote user to SubAdmin
    async createSubAdmin(f_id, e_id) {
        try {
            if (!this.authToken || !this.ins_code) {
                throw new Error('Session missing! Please sign in again.');
            }

            const body = {
                parent: this.ins_code,
                uidToOperate: f_id,
                e_id: e_id,
            };

            //console.log('[WebService] → Creating SubAdmin:', body);

            const response = await this.client.post('/web/createSubAdmin', body, {
                headers: this.headers,
            });

            if (response.status === 200) {
                //console.log('[WebService] ✅ SubAdmin created:', response.data);
                return { success: true, data: response.data };
            }

            return { success: false, message: 'Failed to create subadmin' };
        } catch (error) {
            console.error('[WebService] ❌ Error in createSubAdmin:', error.message);
            return { success: false, error: error.message };
        }
    }

    // 🔹 Revoke Admin privileges (downgrade to faculty)
    async revokeAdmin(f_id, e_id) {
        try {
            if (!this.authToken || !this.ins_code) {
                throw new Error('Session missing! Please sign in again.');
            }

            const body = {
                parent: this.ins_code,
                uidToOperate: f_id,
                e_id: e_id,
            };

            //console.log('[WebService] → Revoking Admin:', body);

            const response = await this.client.post('/web/revokeAdmin', body, {
                headers: this.headers,
            });

            if (response.status === 200) {
                //console.log('[WebService] ✅ Admin revoked:', response.data);
                return { success: true, data: response.data };
            }

            return { success: false, message: 'Failed to revoke admin' };
        } catch (error) {
            console.error('[WebService] ❌ Error in revokeAdmin:', error.message);
            return { success: false, error: error.message };
        }
    }

    // 🔹 Delete Users by E_ID List
    async deleteUser(eids, designation, addToAlumni = false) {
        try {
            if (!Array.isArray(eids) || eids.length === 0) {
                throw new Error("eids must be a non-empty array");
            }
            if (!designation) {
                throw new Error("designation is required (0 for Students, 1 for Teachers)");
            }
            const body = {
                e_ids: eids,
                designation,
                parent: this.ins_code,
                add_to_alumni: addToAlumni,
            };
            const response = await this.client.post("/web/deleteUser", body, {
                headers: this.headers,
            });
            if (response.status === 200 && response.data?.status === "success") {
                return { success: true, data: response.data };
            }
            console.warn("[WebService] ⚠️ Delete failed:", response.data);
            return { success: false, message: response.data?.message || "Unknown delete failure" };
        } catch (error) {
            console.error("[WebService] ❌ Error in deleteUser:", error.message);
            return { success: false, error: error.message };
        }
    }



    // 🔹 Revoke SubAdmin privileges (downgrade to faculty)
    async revokeSubAdmin(f_id, e_id) {
        try {
            if (!this.authToken || !this.ins_code) {
                throw new Error('Session missing! Please sign in again.');
            }

            const body = {
                parent: this.ins_code,
                uidToOperate: f_id,
                e_id: e_id,
            };

            //console.log('[WebService] → Revoking SubAdmin:', body);

            const response = await this.client.post('/web/revokeSubAdmin', body, {
                headers: this.headers,
            });

            if (response.status === 200) {
                //console.log('[WebService] ✅ SubAdmin revoked:', response.data);
                return { success: true, data: response.data };
            }

            return { success: false, message: 'Failed to revoke subadmin' };
        } catch (error) {
            console.error('[WebService] ❌ Error in revokeSubAdmin:', error.message);
            return { success: false, error: error.message };
        }
    }

    // 🔹 Revoke or restore placement access for a student
    async revokeJobAccess(targetUid, revoke) {
        try {
            if (!this.authToken || !this.ins_code) {
                throw new Error('Session missing! Please sign in again.');
            }

            const body = {
                targetUid,
                revoke,
            };

            const response = await this.client.post('/placementPortal/revokeAccess', body, {
                headers: this.headers,
            });

            if (response.status === 200) {
                return { success: true, data: response.data };
            }

            return { success: false, message: 'Failed to update placement access' };

        } catch (error) {
            console.error('[WebService] ❌ Error in revokeJobAccess:', error.message);
            return { success: false, error: error.message };
        }
    }
}
